// ============================================
// Admin Create Event
// ============================================

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { EVENT_DEFAULTS } from '../../types';

export default function AdminEventCreate() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const [formData, setFormData] = React.useState({
    name: '',
    venueName: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    startDate: '',
    endDate: '',
    city: '',
    country: '',
    tablesCount: 20,
    seatsPerTable: 9,
    assignmentMode: 'open_queue' as const,
    pricePerMinute: EVENT_DEFAULTS.pricePerMinute,
    currency: EVENT_DEFAULTS.currency,
    therapistBillPct: EVENT_DEFAULTS.therapistBillPct,
    eventBillPct: EVENT_DEFAULTS.eventBillPct,
    therapistTipPct: EVENT_DEFAULTS.therapistTipPct,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const eventData = {
        ...formData,
        status: 'draft',
        acceptWindowMin: EVENT_DEFAULTS.acceptWindowMin,
        orangeThresholdMin: EVENT_DEFAULTS.orangeThresholdMin,
        offlineThresholdSec: EVENT_DEFAULTS.offlineThresholdSec,
        allowPlayerLocationChanges: EVENT_DEFAULTS.allowPlayerLocationChanges,
        locationChangeMaxCount: EVENT_DEFAULTS.locationChangeMaxCount,
        locationChangeCooldownSec: EVENT_DEFAULTS.locationChangeCooldownSec,
        allowUnderpayment: EVENT_DEFAULTS.allowUnderpayment,
        requireUnderpaymentReason: EVENT_DEFAULTS.requireUnderpaymentReason,
        bookingMinuteOptions: EVENT_DEFAULTS.bookingMinuteOptions,
        extendMinuteOptions: EVENT_DEFAULTS.extendMinuteOptions,
        createdAt: serverTimestamp(),
        createdBy: user?.uid,
      };

      const docRef = await addDoc(collection(db, 'events'), eventData);
      navigate(`/admin/events/${docRef.id}`);
    } catch (err: any) {
      console.error('Error creating event:', err);
      setError(err.message || 'Failed to create event');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-event-create">
      <div className="page-header">
        <h1>Create Event</h1>
      </div>

      <form onSubmit={handleSubmit} className="event-form">
        {error && <div className="error-message">{error}</div>}

        <section className="form-section">
          <h2>Basic Info</h2>
          <div className="form-group">
            <label htmlFor="name">Event Name</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="e.g., EPT Barcelona 2024"
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="venueName">Venue Name</label>
            <input
              type="text"
              id="venueName"
              name="venueName"
              value={formData.venueName}
              onChange={handleChange}
              placeholder="e.g., Casino Barcelona"
              required
            />
          </div>
        </section>

        <section className="form-section">
          <h2>Date & Location</h2>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="startDate">Start Date</label>
              <input
                type="date"
                id="startDate"
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label htmlFor="endDate">End Date</label>
              <input
                type="date"
                id="endDate"
                name="endDate"
                value={formData.endDate}
                onChange={handleChange}
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="city">City</label>
              <input
                type="text"
                id="city"
                name="city"
                value={formData.city}
                onChange={handleChange}
                placeholder="e.g., Barcelona"
              />
            </div>
            <div className="form-group">
              <label htmlFor="country">Country</label>
              <input
                type="text"
                id="country"
                name="country"
                value={formData.country}
                onChange={handleChange}
                placeholder="e.g., Spain"
              />
            </div>
          </div>
        </section>

        <section className="form-section">
          <h2>Layout</h2>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="tablesCount">Number of Tables</label>
              <input
                type="number"
                id="tablesCount"
                name="tablesCount"
                value={formData.tablesCount}
                onChange={handleChange}
                min="1"
                max="500"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="seatsPerTable">Seats per Table</label>
              <input
                type="number"
                id="seatsPerTable"
                name="seatsPerTable"
                value={formData.seatsPerTable}
                onChange={handleChange}
                min="2"
                max="12"
                required
              />
            </div>
          </div>
        </section>

        <section className="form-section">
          <h2>Assignment Mode</h2>
          <div className="form-group">
            <label htmlFor="assignmentMode">How are therapists assigned?</label>
            <select
              id="assignmentMode"
              name="assignmentMode"
              value={formData.assignmentMode}
              onChange={handleChange}
            >
              <option value="open_queue">
                Open Queue - All on-duty therapists see requests
              </option>
              <option value="auto_assign">
                Auto Assign - System assigns based on availability
              </option>
            </select>
          </div>
        </section>

        <section className="form-section">
          <h2>Pricing</h2>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="currency">Currency</label>
              <select
                id="currency"
                name="currency"
                value={formData.currency}
                onChange={handleChange}
              >
                <option value="EUR">EUR (€)</option>
                <option value="USD">USD ($)</option>
                <option value="GBP">GBP (£)</option>
                <option value="NOK">NOK (kr)</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="pricePerMinute">Price per Minute</label>
              <input
                type="number"
                id="pricePerMinute"
                name="pricePerMinute"
                value={formData.pricePerMinute}
                onChange={handleChange}
                min="0.5"
                max="20"
                step="0.5"
                required
              />
            </div>
          </div>
        </section>

        <section className="form-section">
          <h2>Revenue Split</h2>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="therapistBillPct">Therapist Bill Share</label>
              <select
                id="therapistBillPct"
                name="therapistBillPct"
                value={formData.therapistBillPct}
                onChange={handleChange}
              >
                <option value="0.4">40%</option>
                <option value="0.5">50%</option>
                <option value="0.6">60%</option>
                <option value="0.7">70%</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="eventBillPct">Event Bill Share</label>
              <input
                type="text"
                value={`${(1 - formData.therapistBillPct) * 100}%`}
                disabled
              />
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="therapistTipPct">Therapist Tip Share</label>
            <select
              id="therapistTipPct"
              name="therapistTipPct"
              value={formData.therapistTipPct}
              onChange={handleChange}
            >
              <option value="1.0">100% (all tips to therapist)</option>
              <option value="0.9">90%</option>
              <option value="0.8">80%</option>
            </select>
          </div>
        </section>

        <div className="form-actions">
          <button
            type="button"
            onClick={() => navigate('/admin/events')}
            className="btn btn-secondary"
          >
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Creating...' : 'Create Event'}
          </button>
        </div>
      </form>
    </div>
  );
}
